import api from "../api.js";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";

/**
 * Day.js Locales
 * See https://github.com/iamkun/dayjs/tree/dev/src/locale
 */
import "dayjs/locale/cy"; // Welsh
import "dayjs/locale/en-au"; // English (Australia)
import "dayjs/locale/en-gb"; // English (United Kingdom)

/**
 * List of localised formats
 * See https://day.js.org/docs/en/display/format#localized-formats
 * 
 * Don't add any localised patterns!
 */
const DateTimeFormats = {
	FullDateTime: "LLLL",
	Iso8601: "YYYY-MM-DD",
	LongDate: "LL",
	LongTime: "LTS",
	LongWeekday: "dddd",
	ShortDate: "L",
	ShortTime: "LT",
	ShortWeekday: "ddd"
};


/**
 * Format a number as currency in the current locale.
 * 
 * @param {number | bigint} value The currency in minor units (e.g. pence, cents)
 * @param {number | bigint} minimumFractionDigits The minimum number of fraction digits to use; the default for currency formatting is the number of minor unit digits provided by the ISO 4217 currency code list
 * @returns {string} The formatted currency
 */
const formatCurrency = (value, minimumFractionDigits = undefined) => {

	// eslint-disable-next-line no-undef
	return new Intl.NumberFormat(api.locale,
		{
			style: "currency",
			currency: api.currencyUnit,
			minimumFractionDigits
		}).format(value / api.minorUnitDivisor);

};


/**
 * Gets the currency symbol for the current locale.
 * 
 * @returns {string} The currency symbol for the current locale
 */
const getCurrencySymbol = () => {

	// eslint-disable-next-line no-undef
	const parts = new Intl.NumberFormat(api.locale,
		{
			style: "currency",
			currency: api.currencyUnit
		}).formatToParts(0);

	// Split object in to array of strings
	const partStrings = parts.map(part => part.value);

	// Return first part
	return partStrings[0];
};


/**
 * Format a date in the current locale
 * 
 * @param {dayjs.ConfigType} value The date/time to format
 * @param {DateTimeFormats} format The localised format to use
 * @returns {string} The formatted date
 */
const formatDateTime = (value, format = DateTimeFormats.ShortDate) => {

	dayjs.extend(localizedFormat);
	const dayJSDate = dayjs(value);
	return dayJSDate.locale(api.locale).format(format);
};


export {DateTimeFormats, getCurrencySymbol, formatCurrency, formatDateTime};
